import React from 'react';
import { useTranslation } from '../LanguageContext';

/**
 * A track record, shown two ways.
 *
 * `ReputationBadge` is the inline chip that follows a name around — applicant
 * rows, campaign cards, deal headers. `ReputationPanel` is the full read on a
 * profile or press page.
 *
 * Both keep the same split the API makes: **what people said** (stars, written
 * reviews) and **what the platform can attest** (how fast a brand reviewed, how
 * often a creator hit the draft date). The second is the reason a creator can
 * trust the first — opinions can be gamed, an escrow timeline cannot — so it is
 * labelled and never blended into the star average.
 */

const STAR_PATH = 'M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z';

export function Stars({ value = 0, size = 'w-3.5 h-3.5', interactive = false, onPick }) {
    return (
        <span className="inline-flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map(star => {
                // Partial fill would be prettier and less honest at this size —
                // a rounded star is easier to read than a 62%-wide sliver, and
                // the numeric average is printed beside it anyway.
                const filled = value >= star - 0.25;
                const Node = interactive ? 'button' : 'span';
                return (
                    <Node key={star} type={interactive ? 'button' : undefined}
                        onClick={interactive ? () => onPick(star) : undefined}
                        aria-label={interactive ? `${star}` : undefined}
                        className={interactive ? 'transition hover:scale-110' : ''}>
                        <svg xmlns="http://www.w3.org/2000/svg"
                            className={`${size} ${filled ? 'text-amber-400' : 'text-slate-200'}`}
                            fill="currentColor" viewBox="0 0 20 20">
                            <path d={STAR_PATH} />
                        </svg>
                    </Node>
                );
            })}
        </span>
    );
}

/** The chip. Renders nothing at all for an account with no history — an
 *  empty "0.0 ★" would read as a bad score rather than as no score. */
export function ReputationBadge({ rating, compact = false }) {
    const { t } = useTranslation();
    if (!rating || !rating.count) return null;

    return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 border border-amber-200 flex-shrink-0"
            title={`${rating.average} / 5 · ${rating.count} ${t('reputation.reviews')}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 text-amber-500"
                fill="currentColor" viewBox="0 0 20 20">
                <path d={STAR_PATH} />
            </svg>
            <span className="text-[10px] font-black text-amber-800 tabular-nums">{rating.average}</span>
            {!compact && (
                <span className="text-[10px] text-amber-600/80 tabular-nums">({rating.count})</span>
            )}
        </span>
    );
}

function Bar({ label, value, max = 5, tone = 'bg-primary-500' }) {
    if (value === null || value === undefined) return null;
    return (
        <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-500 w-28 flex-shrink-0 truncate">{label}</span>
            <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full rounded-full ${tone}`}
                    style={{ width: `${Math.min(100, (value / max) * 100)}%` }} />
            </div>
            <span className="text-[11px] font-bold text-slate-700 tabular-nums w-7 text-right">{value}</span>
        </div>
    );
}

/** One platform-held fact. Deliberately plain — these are the numbers that
 *  do not need selling. */
function Fact({ value, label, tone = 'text-slate-800' }) {
    if (value === null || value === undefined) return null;
    return (
        <div className="rounded-xl bg-white border border-slate-200 p-2.5 text-center">
            <p className={`text-base font-black tabular-nums ${tone}`}>{value}</p>
            <p className="text-[10px] text-slate-500 font-semibold leading-tight mt-0.5">{label}</p>
        </div>
    );
}

export function ReputationPanel({ reputation, className = '', showReviews = true }) {
    const { t } = useTranslation();
    if (!reputation) return null;

    const { count, average, distribution = {}, criteria = {},
        criteria_keys: keys = [], facts, reviews = [] } = reputation;
    const isBrand = reputation.account_type === 'brand';

    if (!count && !facts) {
        return (
            <div className={`rounded-2xl border border-slate-200 bg-white p-4 ${className}`}>
                <h3 className="text-sm font-bold text-slate-800">{t('reputation.title')}</h3>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                    {t('reputation.emptyNote')}
                </p>
            </div>
        );
    }

    const peak = Math.max(...Object.values(distribution), 1);

    return (
        <div className={`rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 ${className}`}>
            <h3 className="text-lg font-bold text-slate-800">{t('reputation.title')}</h3>
            <p className="text-xs text-slate-400 mb-4">
                {isBrand ? t('reputation.brandHint') : t('reputation.creatorHint')}
            </p>

            {count > 0 && (
                <div className="flex items-start gap-5 flex-wrap">
                    <div className="text-center flex-shrink-0">
                        <p className="text-4xl font-black text-slate-900 tabular-nums leading-none">{average}</p>
                        <div className="mt-1.5"><Stars value={average} /></div>
                        <p className="text-[11px] text-slate-400 mt-1">
                            {count} {t('reputation.reviews')}
                        </p>
                    </div>

                    <div className="flex-1 min-w-[160px] space-y-1">
                        {[5, 4, 3, 2, 1].map(star => (
                            <div key={star} className="flex items-center gap-2">
                                <span className="text-[10px] text-slate-400 w-3 tabular-nums">{star}</span>
                                <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                    <div className="h-full rounded-full bg-amber-400"
                                        style={{ width: `${((distribution[star] || 0) / peak) * 100}%` }} />
                                </div>
                                <span className="text-[10px] text-slate-400 w-4 text-right tabular-nums">
                                    {distribution[star] || 0}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {reputation.would_work_again_percent !== null &&
                reputation.would_work_again_percent !== undefined && (
                    <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-2.5 flex items-center gap-2">
                        <span className="text-lg font-black text-emerald-700 tabular-nums">
                            %{reputation.would_work_again_percent}
                        </span>
                        <span className="text-xs text-emerald-800 leading-tight">
                            {t('reputation.wouldWorkAgain')}
                        </span>
                    </div>
                )}

            {keys.length > 0 && count > 0 && (
                <div className="mt-4 space-y-1.5">
                    {keys.map(key => (
                        <Bar key={key} label={t(`reputation.criterion_${key}`)} value={criteria[key]} />
                    ))}
                </div>
            )}

            {/* Platform-held facts. Fenced off from the star scores above
                because nobody can talk their way out of these. */}
            {facts && (
                <div className="mt-5 pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-1.5 mb-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-primary-600"
                            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wide">
                            {t('reputation.factsTitle')}
                        </p>
                    </div>
                    <p className="text-[11px] text-slate-400 mb-3 leading-relaxed">
                        {t('reputation.factsHint')}
                    </p>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <Fact value={facts.deals} label={t('reputation.factDeals')} />
                        {isBrand ? (
                            <>
                                <Fact value={facts.reviewed_in_time_percent !== null
                                    ? `%${facts.reviewed_in_time_percent}` : null}
                                    label={t('reputation.factReviewedInTime')}
                                    tone={facts.reviewed_in_time_percent >= 80
                                        ? 'text-emerald-600' : 'text-amber-600'} />
                                <Fact value={facts.avg_review_hours !== null
                                    ? `${facts.avg_review_hours}s` : null}
                                    label={t('reputation.factReviewSpeed')} />
                                <Fact value={`%${facts.dispute_percent}`}
                                    label={t('reputation.factDisputes')}
                                    tone={facts.dispute_percent > 0 ? 'text-amber-600' : 'text-slate-800'} />
                            </>
                        ) : (
                            <>
                                <Fact value={facts.on_time_percent !== null
                                    ? `%${facts.on_time_percent}` : null}
                                    label={t('reputation.factOnTime')}
                                    tone={facts.on_time_percent >= 80
                                        ? 'text-emerald-600' : 'text-amber-600'} />
                                <Fact value={facts.avg_turnaround_days !== null
                                    ? `${facts.avg_turnaround_days}g` : null}
                                    label={t('reputation.factTurnaround')} />
                                <Fact value={`%${facts.dispute_percent}`}
                                    label={t('reputation.factDisputes')}
                                    tone={facts.dispute_percent > 0 ? 'text-amber-600' : 'text-slate-800'} />
                            </>
                        )}
                    </div>

                    {isBrand && facts.lapsed_reviews > 0 && (
                        /* The single most useful thing on this panel for a
                           creator deciding whether to apply. */
                        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2.5 leading-relaxed">
                            {facts.lapsed_reviews} {t('reputation.lapsedWarning')}
                        </p>
                    )}
                </div>
            )}

            {showReviews && reviews.length > 0 && (
                <div className="mt-5 pt-4 border-t border-slate-100 space-y-2.5 max-h-96 overflow-y-auto custom-scrollbar pr-1">
                    {reviews.map(review => (
                        <div key={review.id} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                            <div className="flex items-center gap-2 mb-1">
                                <img src={review.rater?.profile_picture || '/logo192.png'} alt=""
                                    className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
                                <span className="text-xs font-bold text-slate-700 truncate">
                                    {review.rater?.username}
                                </span>
                                <Stars value={review.overall} size="w-3 h-3" />
                                <span className="text-[10px] text-slate-400 ml-auto flex-shrink-0">
                                    {new Date(review.created_at).toLocaleDateString('en-GB')}
                                </span>
                            </div>
                            <p className="text-[10px] text-slate-400 mb-1">{review.campaign}</p>
                            {review.comment && (
                                <p className="text-sm text-slate-600 leading-relaxed">"{review.comment}"</p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default ReputationBadge;
